import { StarsButton, Translation } from '@ps/web-shared-components';
import { StarsInfo, createForm } from '@ps/web-shared-ui';
import type { SubmissionErrors, ValidationErrors } from 'final-form';
import { FORM_ERROR } from 'final-form';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import useRequest from '@swf/core/hooks/useRequest';
import type { Dispatch } from '@swf/core/store';

import sharedStyles from '../../../styles/styles.scss';

import {
  TXTCLI_Confirm,
  TXTWCL_Gaming_limits_apply_to_the_gam_ELL,
  TXTWCL_Something_went_wrong
} from './ESGamingLimitForm.messages.json';
import styles from './ESGamingLimitForm.scss';
import FuturePlayRestrictionSection from './FuturePlayRestrictionSection/FuturePlayRestrictionSection';
import NetLossLimitField from './NetLossLimitField/NetLossLimitField';
import SaferGamingMessage from './SaferGamingMessage/SaferGamingMessage';
import TimeLimitSlider from './TimeLimitSlider/TimeLimitSlider';

export interface FormValues {
  timeLimit: number;
  lossLimit: string;
  enableBlockAmount: boolean;
  blockAmount: null | number;
}

const Form = createForm<FormValues>();

interface Props {
  onSuccess?(): void;
}

const ESGamingLimitForm: React.FunctionComponent<Props> = ({ onSuccess }) => {
  const [lastSessionEndTime, setLastSessionEndTime] = useState<Nullable<number>>(null);
  const request = useRequest();
  const { modal } = useDispatch<Dispatch>();

  const validate = ({ timeLimit, enableBlockAmount, blockAmount }: FormValues) => {
    const errors: ValidationErrors = {
      ...(timeLimit < 30 && {
        timeLimit: {
          valid: false
        }
      }),
      ...(enableBlockAmount &&
        !blockAmount && {
          blockAmount: {
            valid: false
          }
        })
    };
    return errors;
  };

  const handleSubmit = (formValues: FormValues, callback?: (errors?: SubmissionErrors) => void) => {
    const { blockAmount, lossLimit, timeLimit } = formValues;
    request({
      service: 'responsiblegaming.setessessionlimits',
      body: { ...(blockAmount && { blockAmount }), lossLimit: Number(lossLimit) * 100, timeLimit }
    }).subscribe(
      () => {
        modal.close('/notifications/es-gaming-limit');
        onSuccess && onSuccess();
      },
      () => callback?.({ [FORM_ERROR]: <Translation {...TXTWCL_Something_went_wrong} /> })
    );
  };

  useEffect(() => {
    request({ service: 'responsiblegaming.checkessessionlimits' }).subscribe(
      ({ body: { lastSessionEnd } }) => {
        setLastSessionEndTime(lastSessionEnd);
      },
      function (err) {
        // do nothing
      }
    );
  }, [request]);

  const initialFormValues: FormValues = {
    timeLimit: 0,
    lossLimit: '',
    enableBlockAmount: false,
    blockAmount: null
  };

  return (
    <Form
      onSubmit={(values, _, callback) => handleSubmit(values, callback)}
      validate={validate}
      initialValues={initialFormValues}
      render={({ submitting, hasValidationErrors, submitError }) => (
        <div className={styles.formWrapper}>
          {submitError && <StarsInfo type="error">{submitError}</StarsInfo>}
          {lastSessionEndTime && <SaferGamingMessage lastSessionEndTime={lastSessionEndTime} />}
          <Translation {...TXTWCL_Gaming_limits_apply_to_the_gam_ELL} />
          <TimeLimitSlider />
          <NetLossLimitField />
          <FuturePlayRestrictionSection />
          <div>
            <StarsButton
              block
              loading={submitting}
              disabled={hasValidationErrors}
              type="submit"
              className={sharedStyles.submitBtn}
              translation={TXTCLI_Confirm}
            />
          </div>
        </div>
      )}
    />
  );
};

export default ESGamingLimitForm;
